import { useState, useMemo, Fragment, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';

import { useQuery } from '@tanstack/react-query';
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  Transition,
} from '@headlessui/react';
import { Indicator, Spinner, Divider, CloseButton } from '@crazyegginc/hatch';

import { useSelectedSite } from '/src/hooks';
import { goalSelectorListQuery } from '/src/features/goals/queries';
import { WebP } from './WebP';
import { getGoalTriggerMetadata } from '/src/features/goals/triggerMetadata';
import { GoalTriggerSummary } from '/src/features/goals/components/GoalTriggerSummary';
import { getGoalTriggerType } from '/src/features/goals/utils';
import { ENTER_SITE } from '../features/funnels/constants';

import { ReactComponent as TickIcon } from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg';
import { ReactComponent as LockIcon } from '@crazyegginc/hatch/dist/images/icon-lock-filled.svg';
import { ReactComponent as SearchIcon } from '@crazyegginc/hatch/dist/images/icon-search-filled.svg';

import BlockPNG from '@crazyegginc/hatch/dist/images/illustration-block.png';
import BlockWebP from '@crazyegginc/hatch/dist/images/illustration-block.webp';
import EnterSitePNG from '@crazyegginc/hatch/dist/images/illustration-enter-site.png';
import EnterSiteWebP from '@crazyegginc/hatch/dist/images/illustration-enter-site.webp';
import { AppLink } from '/src/features/_global/components/AppLink';

export function SearchableGoalSelector({
  error,
  value,
  onSelect,
  disabled,
  filterIds = [],
  showNoGoalOption = true,
  showEnterSiteStep,
  enterSiteDisabledReason,
  displayIfNoGoals,
  comboboxOptionsClassName,
  placeholder = 'Select a Goal',
  showSearchIcon,
  showSelected = true,
  openOnMount,
  clearButton = false,
}) {
  const { selectedSite } = useSelectedSite();
  const [query, setQuery] = useState('');
  const inputRef = useRef();

  const {
    data: goalsData,
    isFetching: fetchingGoals,
    isInitialLoading,
  } = useQuery({
    ...goalSelectorListQuery({ siteId: selectedSite?.id }),
    enabled: Boolean(selectedSite?.id),
  });

  const goals = useMemo(() => {
    const result =
      goalsData?.goalSelectorList
        .filter((x) => !filterIds.includes(x.id))
        .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0)) ?? [];
    if (showEnterSiteStep) {
      result.unshift({ id: ENTER_SITE, name: ENTER_SITE, disabled: !!enterSiteDisabledReason });
    }
    return result;
  }, [goalsData, filterIds, showEnterSiteStep, enterSiteDisabledReason]);

  const selectedGoal = goals.find((g) => g.id === value) ?? null;

  const filteredGoals =
    query === ''
      ? goals
      : goals.filter((goal) =>
          goal.name.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, '')),
        );

  useEffect(() => {
    if (value && !fetchingGoals && !disabled && !selectedGoal) {
      onSelect?.(null);
    }
  }, [onSelect, disabled, selectedGoal, fetchingGoals, value]);

  const onAttach = useCallback(
    (n) => {
      if (n) {
        inputRef.current = n;
        if (openOnMount) {
          setTimeout(() => {
            n.focus();
            if (n.value === placeholder) {
              n.select();
            }
          }, 1);
        }
      }
    },
    [openOnMount, placeholder],
  );

  if (!goals.length && !value && !fetchingGoals) {
    if (displayIfNoGoals == null) return displayIfNoGoals;
  }

  const displayValue = (goal) => {
    if (value != null && !selectedGoal) return 'The Conversion has been deleted';
    if (!fetchingGoals && !goals.length) {
      return displayIfNoGoals;
    }
    if (goal) return goal.name;
    if (showNoGoalOption) return 'No Conversion';
    return placeholder;
  };

  return (
    <div className="text-body-2 w-full">
      <Combobox
        immediate={openOnMount}
        value={selectedGoal}
        onChange={onSelect}
        by="id"
        disabled={disabled || (!goals.length && !fetchingGoals)}
      >
        {({ open }) => (
          <div className="relative">
            <div
              className={classNames(
                'text-body-2 relative h-10 w-full cursor-default rounded border text-left focus:outline-none',
                {
                  'focus:border-dodger-blue-500': !error,
                  'border-dodger-blue-500': open && !error,
                  'border-mystic-500': !open && !error,
                  'bg-white text-black-pearl-500': !disabled,
                  'bg-white-lilac-500 text-lynch-500': disabled,
                  'border-carnation-500 focus:border-carnation-500': !!error,
                },
              )}
            >
              {isInitialLoading ? (
                <div className="flex h-full w-full items-center justify-center space-x-2.5">
                  <Spinner />
                  <span className="text-lynch-500">Loading Conversion Events...</span>
                </div>
              ) : (
                <>
                  {showSearchIcon ? (
                    <SearchIcon className="h-4 w-4 fill-current text-cadet-blue-500 absolute top-3 left-[22px]" />
                  ) : null}
                  <ComboboxInput
                    className={classNames(
                      'w-full rounded border-none py-2 pr-14 text-sm leading-none focus:ring-0 h-8 mt-1',
                      {
                        'pl-[50px]': showSearchIcon,
                        'pl-3': !showSearchIcon,
                      },
                    )}
                    aria-label="search for goal"
                    displayValue={displayValue}
                    onChange={(event) => setQuery(event.target.value)}
                    ref={onAttach}
                    onFocus={(e) =>
                      setTimeout(() => {
                        if (e.target.value === placeholder) {
                          e.target.select();
                        }
                      }, 10)
                    }
                    onClick={(e) => {
                      if (e.target.value === placeholder) {
                        e.target.select();
                      }
                    }}
                  />
                  <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-cadet-blue-500 hover:text-black-pearl-500">
                    <Indicator type="dropdown" />
                  </ComboboxButton>
                  {clearButton && query.length > 0 ? (
                    <CloseButton
                      buttonClass="!top-1/2 -translate-y-1/2 !right-7 !mr-0 !my-0 group !h-3.5 !w-3.5"
                      iconClass="group-hover:text-radical-red-500 !h-2.5 !w-2.5"
                      label="clear search"
                      onClick={() => {
                        setQuery('');
                        inputRef.current.focus();
                      }}
                    />
                  ) : null}
                </>
              )}
            </div>
            <Transition
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              afterLeave={() => setQuery('')}
            >
              <ComboboxOptions
                className={classNames(
                  'absolute z-10 mt-1 max-h-[400px] w-full overflow-auto rounded-md bg-white py-1 shadow-lg',
                  'ring-1 ring-black ring-opacity-5 focus:outline-none',
                  comboboxOptionsClassName,
                )}
              >
                {filteredGoals.length === 0 && query !== '' ? (
                  <div className="text-body-3 relative flex w-full cursor-default select-none justify-center px-4 py-2">
                    Nothing found.
                  </div>
                ) : (
                  <>
                    {!!showNoGoalOption && query === '' && (
                      <>
                        <ComboboxOption
                          className={({ focus, disabled }) =>
                            classNames('relative cursor-default select-none py-3.75 pr-4', {
                              'bg-solitude-500': focus,
                              'bg-white-lilac-500 text-lynch-500': disabled,
                              'pl-10': showSelected,
                              'pl-4': !showSelected,
                            })
                          }
                          value={null}
                        >
                          {({ selected }) => (
                            <>
                              <GoalItem webp={BlockWebP} png={BlockPNG} name="No conversion">
                                {selected ? (
                                  <span className={`absolute inset-y-0 left-0 z-20 flex items-center pl-3`}>
                                    <TickIcon className="h-2.5 w-2.5 fill-current text-dodger-blue-500" />
                                  </span>
                                ) : null}
                              </GoalItem>
                            </>
                          )}
                        </ComboboxOption>
                        <Divider dashed className="!w-auto last:hidden" />
                      </>
                    )}
                    {filteredGoals.map((goal) => {
                      const goalTriggerType = getGoalTriggerType(goal);
                      const goalMetadata = getGoalTriggerMetadata(goalTriggerType);

                      return (
                        <Fragment key={goal.id}>
                          <ComboboxOption
                            className={({ focus }) =>
                              classNames('relative cursor-default select-none py-3.75 pr-4', {
                                'bg-solitude-500': focus,
                                'pl-10': showSelected,
                                'pl-4': !showSelected,
                              })
                            }
                            value={goal}
                            disabled={goal.disabled}
                          >
                            {({ selected, disabled }) =>
                              goal.id === ENTER_SITE ? (
                                <>
                                  <GoalItem
                                    webp={EnterSiteWebP}
                                    png={EnterSitePNG}
                                    name={goal.name}
                                    disabled={disabled}
                                  >
                                    <div className={classNames('text-body-2 !text-lynch-500')}>
                                      Visits anywhere on your site
                                    </div>
                                    {disabled ? (
                                      <div className="bg-white-lilac-500 rounded-[3px] flex items-center p-1.25 text-body-2 italic whitespace-pre text-lynch-500">
                                        <LockIcon className="fill-current text-cadet-blue-500 w-3.5 h-3.5 mr-1.25" />
                                        {enterSiteDisabledReason === 'ENABLE_WEB_ANALYTICS' ? (
                                          <>
                                            <span>Enable </span>
                                            <AppLink
                                              to="/web-analytics"
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-dodger-blue-500"
                                            >
                                              Web Analytics
                                            </AppLink>
                                            <span> to use this step.</span>
                                          </>
                                        ) : null}
                                        {enterSiteDisabledReason === 'NOT_STEP_ONE' ? (
                                          <span>Available for step 1 only.</span>
                                        ) : null}
                                      </div>
                                    ) : null}
                                  </GoalItem>
                                </>
                              ) : (
                                <>
                                  <GoalItem webp={goalMetadata.webp} png={goalMetadata.png} name={goal.name}>
                                    <GoalTriggerSummary
                                      goal={goal}
                                      showMaxTriggers={2}
                                      showIntroText={false}
                                      className="!text-sm !text-lynch-500"
                                    />
                                  </GoalItem>
                                  {selected && showSelected ? (
                                    <span className={`absolute inset-y-0 left-0 z-20 flex items-center pl-3`}>
                                      <TickIcon className="h-2.5 w-2.5 fill-current text-dodger-blue-500" />
                                    </span>
                                  ) : null}
                                </>
                              )
                            }
                          </ComboboxOption>
                          <Divider dashed className="!w-auto last:hidden" />
                        </Fragment>
                      );
                    })}
                  </>
                )}
              </ComboboxOptions>
            </Transition>
          </div>
        )}
      </Combobox>
      {!!error && <div className="text-error absolute">{error}</div>}
    </div>
  );
}

function GoalItem({ webp, png, name, disabled, children }) {
  return (
    <div className="flex items-start">
      <WebP
        className={classNames('w-[30px] flex items-center shrink-0', {
          'grayscale ': disabled,
        })}
        imageClassName="w-[30px]"
        webp={webp}
        fallback={png}
        alt="goal type"
        width="30px"
      />
      <div className="ml-2.5">
        <span
          className={classNames('text-body-1', {
            '!text-lynch-500': disabled,
          })}
        >
          {name}
        </span>
        {children}
      </div>
    </div>
  );
}
